import { useCallback, useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { CreateJobCodeRequest, Guid, JobCode, UpdateJobCodeRequest } from "@/api/types";

export type JobCodesStatus = "loading" | "ready" | "error";

export interface UseJobCodesResult {
  /** `null` until resolved and on failure. Active job codes only — never includes an
   *  archived row, which lives in `archived` below. */
  active: JobCode[] | null;
  /** Always `[]` rather than `null`: the archived disclosure has a real, always-valid
   *  answer ("nothing archived yet") even while `active` is still loading or failed. */
  archived: JobCode[];
  status: JobCodesStatus;
  error: string | null;
  reload: () => Promise<void>;
  /** Throws on failure — JobCodes.tsx catches this itself to read per-field validation
   *  errors off it (`problemFieldErrors`), which this hook has no opinion about. */
  create: (body: CreateJobCodeRequest) => Promise<JobCode>;
  update: (id: Guid, body: UpdateJobCodeRequest) => Promise<JobCode>;
  archive: (id: Guid) => Promise<void>;
}

/**
 * The farm's own job codes (ef-023) — what a day's hours were FOR, distinct from
 * `usePlaces`'s WHERE. One GET, split into active/archived on the client rather than two
 * round trips, so opening the archived disclosure is instant. Mirrors `usePlaces` exactly
 * — same shape, same reasoning, a different table.
 *
 * Owner-only server-side (403 for an employee login). This hook does not check that and
 * must not: a client-side role check is a rendering decision, and treating it as the
 * control is how the real one stops being maintained.
 *
 * Re-reads the WHOLE list after every write rather than patching the array in place —
 * same reasoning `usePlaces` gives: the list is small, and a re-read can never drift from
 * what the server actually holds.
 */
export function useJobCodes(): UseJobCodesResult {
  const [jobCodes, setJobCodes] = useState<JobCode[] | null>(null);
  const [status, setStatus] = useState<JobCodesStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const resolved = await demoApi.getJobCodes(true, signal);
      if (signal?.aborted) return;
      setJobCodes(resolved);
      setStatus("ready");
      setError(null);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      setJobCodes(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the farm's job codes."));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  const create = useCallback(
    async (body: CreateJobCodeRequest): Promise<JobCode> => {
      const jobCode = await demoApi.createJobCode(body);
      await load();
      return jobCode;
    },
    [load],
  );

  const update = useCallback(
    async (id: Guid, body: UpdateJobCodeRequest): Promise<JobCode> => {
      const jobCode = await demoApi.updateJobCode(id, body);
      await load();
      return jobCode;
    },
    [load],
  );

  const archive = useCallback(
    async (id: Guid): Promise<void> => {
      await demoApi.archiveJobCode(id);
      await load();
    },
    [load],
  );

  const active = jobCodes === null ? null : jobCodes.filter((j) => j.archivedAt === null);
  const archived = jobCodes === null ? [] : jobCodes.filter((j) => j.archivedAt !== null);

  return { active, archived, status, error, reload, create, update, archive };
}
